import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { japanAddressData } from '../data/japanAddressData';

const JapanAddressSelector = ({ 
    label, 
    name, 
    value, 
    onChange, 
    required = false, 
    className = "" 
}) => {
    const { t } = useTranslation();
    const [selectedPrefecture, setSelectedPrefecture] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [detailAddress, setDetailAddress] = useState('');
    const [cities, setCities] = useState([]);

    // 从完整地址中解析道都府县、市町、详细地址
    const parseAddress = (fullAddress) => {
        if (!fullAddress) return { prefecture: '', city: '', detail: '' };

        const prefectures = Object.keys(japanAddressData);
        let foundPrefecture = '';
        let foundCity = '';
        let remainingAddress = fullAddress;

        // 查找道都府县
        for (const prefecture of prefectures) {
            if (fullAddress.includes(prefecture)) {
                foundPrefecture = prefecture;
                remainingAddress = fullAddress.replace(prefecture, '');
                break;
            }
        }

        // 如果找到了道都府县，查找市町
        if (foundPrefecture && japanAddressData[foundPrefecture]) {
            const citiesInPrefecture = japanAddressData[foundPrefecture];
            for (const city of citiesInPrefecture) {
                if (remainingAddress.includes(city)) {
                    foundCity = city;
                    remainingAddress = remainingAddress.replace(city, '');
                    break;
                }
            }
        }

        return {
            prefecture: foundPrefecture,
            city: foundCity,
            detail: remainingAddress.trim()
        };
    };

    // 初始化时解析现有地址
    useEffect(() => {
        if (value) {
            const parsed = parseAddress(value);
            setSelectedPrefecture(parsed.prefecture);
            setSelectedCity(parsed.city);
            setDetailAddress(parsed.detail);
            if (parsed.prefecture) {
                setCities(japanAddressData[parsed.prefecture] || []);
            }
        }
    }, [value]);

    // 道都府县改变时
    const handlePrefectureChange = (e) => {
        const prefecture = e.target.value;
        setSelectedPrefecture(prefecture);
        setSelectedCity('');
        setDetailAddress('');
        setCities(prefecture ? japanAddressData[prefecture] || [] : []);
        
        // 更新完整地址
        updateFullAddress(prefecture, '', '');
    };

    // 市町改变时
    const handleCityChange = (e) => {
        const city = e.target.value;
        setSelectedCity(city);
        updateFullAddress(selectedPrefecture, city, detailAddress);
    };

    // 详细地址改变时
    const handleDetailChange = (e) => {
        const detail = e.target.value;
        setDetailAddress(detail);
        updateFullAddress(selectedPrefecture, selectedCity, detail);
    };

    // 更新完整地址
    const updateFullAddress = (prefecture, city, detail) => {
        let fullAddress = '';
        if (prefecture) fullAddress += prefecture;
        if (city) fullAddress += city;
        if (detail) fullAddress += detail;
        
        // 调用父组件的onChange
        const event = {
            target: {
                name: name,
                value: fullAddress
            }
        };
        onChange(event);
    };

    // 清空选择
    const handleClear = () => {
        setSelectedPrefecture('');
        setSelectedCity('');
        setDetailAddress('');
        setCities([]);
        updateFullAddress('', '', '');
    };

    return (
        <div className={className}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {label}{required && '*'}
            </label>
            
            <div className="space-y-3">
                {/* 道都府县选择 */}
                <div>
                    <label className="block text-xs text-gray-600 mb-1">{t('addressSelector.prefecture')}</label>
                    <select
                        value={selectedPrefecture}
                        onChange={handlePrefectureChange}
                        className="w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                        <option value="">{t('addressSelector.selectPrefecture')}</option>
                        {Object.keys(japanAddressData).map(prefecture => (
                            <option key={prefecture} value={prefecture}>
                                {prefecture}
                            </option>
                        ))}
                    </select>
                </div>

                {/* 市町选择 */}
                <div>
                    <label className="block text-xs text-gray-600 mb-1">{t('addressSelector.city')}</label>
                    <select
                        value={selectedCity}
                        onChange={handleCityChange}
                        disabled={!selectedPrefecture}
                        className="w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                    >
                        <option value="">{t('addressSelector.selectCity')}</option>
                        {cities.map(city => (
                            <option key={city} value={city}>
                                {city}
                            </option>
                        ))}
                    </select>
                </div>

                {/* 详细地址输入 */}
                <div>
                    <label className="block text-xs text-gray-600 mb-1">{t('addressSelector.detailAddress')}</label>
                    <input
                        type="text"
                        value={detailAddress}
                        onChange={handleDetailChange}
                        placeholder={t('addressSelector.detailAddressPlaceholder')}
                        className="w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                </div>

                {/* 完全な住所プレビューと操作ボタン */}
                <div className="bg-gray-50 p-2 rounded border">
                    <div className="flex justify-between items-start">
                        <div className="flex-1">
                            <span className="text-xs text-gray-600">{t('addressSelector.fullAddress')}:</span>
                            <div className="text-sm font-medium text-gray-800 mt-1">
                                {value || t('addressSelector.notSelected')}
                            </div>
                        </div>
                        {value && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="ml-2 text-xs text-red-600 hover:text-red-800 underline"
                            >
                                {t('clear')}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* 隐藏的input用于表单提交 */}
            <input
                type="hidden"
                name={name}
                value={value || ''}
                required={required}
            />
        </div>
    );
};

export default JapanAddressSelector;
